/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, FormApi, FormProps } from "@/components/form";
import { localNow } from "@/shared/local-now";
import { z } from "zod/v4";
import { useEffect } from "react";
import { useStore } from "@tanstack/react-form";
import ArticuloTransferenciaGeneralInfoFields from "./_shared/articulo-transferencia-general-info-fields";
import ArticuloTransferenciaArticuloSearch from "./_shared/articulo-transferencia-articulo-search";
import { Stack, Grid2 as Grid } from "@mui/material";
import ArticuloTransferenciaArticuloList from "./_shared/articulo-transferencia-articulo-list";
import { ArticuloTransferenciaTipo } from "@/shared/types/articulo-transferencia";
import { getSaldosDeposito } from "@/features/inventario/articulos/utils/validar-stock-items";

export const articuloTransferenciaFormSchema = z.object({
    fecha: z.iso.datetime({ local: true, message: "Este campo es requerido" }).default(localNow()),
    tipo: z.number().default(ArticuloTransferenciaTipo.Envio),
    depositoOrigenId: z.string().nonempty("Este campo es requerido"),
    depositoDestinoId: z.string().nonempty("Este campo es requerido"),
    items: z.array(z.object({
        articuloId: z.string().nonempty("Este campo es requerido"),
        cantidad: z.number().min(1, "La cantidad debe ser mayor que 0"),
        stockDisponible: z.number().optional()
    })).min(1, "Debe haber por lo menos un ítem")
}).refine(v => !v.depositoOrigenId || !v.depositoDestinoId || v.depositoOrigenId !== v.depositoDestinoId, {
    message: "El depósito de origen y destino deben ser distintos",
    path: ["depositoDestinoId"]
})

interface ArticuloTransferenciaFormProps extends FormProps<z.input<typeof articuloTransferenciaFormSchema>> {
    onCancel?: (form: FormApi) => void
}

export default function ArticuloTransferenciaForm({
    form,
    onCancel,
    ...props
}: ArticuloTransferenciaFormProps) {
    // Refrescar el stockDisponible (del depósito ORIGEN) de los ítems ya cargados cuando cambia
    // el depósito origen; las filas sin stock se marcan visualmente en la lista de artículos.
    const depositoOrigenId = useStore(form.store, s => (s.values as any).depositoOrigenId as string | undefined);

    useEffect(() => {
        if (!depositoOrigenId) return;
        const items = form.getFieldValue("items") ?? [];
        if (items.length <= 0) return;

        let cancelled = false;
        (async () => {
            const saldos = await getSaldosDeposito([...new Set(items.map(i => i.articuloId))], depositoOrigenId);
            if (cancelled) return;

            const currentItems = form.getFieldValue("items") ?? [];
            form.setFieldValue("items", currentItems.map(i =>
                saldos.has(i.articuloId) ? { ...i, stockDisponible: saldos.get(i.articuloId) } : i
            ));
        })();

        return () => { cancelled = true; };
    }, [depositoOrigenId]);

    return <Form form={form} gap={3} {...props}>
        <ArticuloTransferenciaGeneralInfoFields />
        <Grid container columns={{ xs: 1, md: 3 }} spacing={5} flex={1}>
            <Grid component={Stack} size={1}>
                <ArticuloTransferenciaArticuloSearch
                    onArticuloSelect={value => form.pushFieldValue("items", {
                        articuloId: value.articulo.id,
                        articulo: value.articulo,
                        cantidad: value.cantidad,
                        stockDisponible: value.articulo.cantidad
                    } as any)}
                />
            </Grid>
            <Grid component={Stack} size={2} gap={3}>
                <form.Field name="items" mode="array">
                    {field => <ArticuloTransferenciaArticuloList
                        items={field.state.value}
                        remove={field.removeValue}
                    />}
                </form.Field>
                <Stack direction={"row"} justifyContent={"end"} gap={3}>
                    <Form.Button onClick={() => onCancel && onCancel(form)}>Cancelar</Form.Button>
                    <Form.SubmitButton>Guardar</Form.SubmitButton>
                </Stack>
            </Grid>
        </Grid>
    </Form>
}